import type { AnchorHTMLAttributes, ReactNode, Ref } from 'react'
import { Link } from 'react-router'
import { useButtonSkin, type ButtonVariant, type ButtonSize } from './buttonSkin'
import './primitives.css'

type Variant = ButtonVariant
type Size = ButtonSize

/**
 * A DESTINATION wearing the button skin — the anchor half of `Button`.
 *
 * **Written because the fourth hand-copy shipped a defect.** `Button` renders a
 * `<button>` unconditionally, so four screens independently wrote out its class
 * list and its inline `min-height` by hand:
 *
 *   AppCrashed      `<a href>`  — remembered the min-height
 *   Maintenance     `<a href>`  — remembered it
 *   Merchants       `<Link>`    — remembered it
 *   NotFound        `<Link>`    — **forgot it**
 *
 * That omission is issue #334: both `NotFound` recoveries measured 42.8px
 * against M0-R29's 44px floor at every phone width, on `/no-such-route` and on
 * `/__probe?screen=notfound` — sixteen of the sweep's seventeen failures, from
 * one missing prop. Nothing but the browser sweep could see it: the classes are
 * correct, the skin is correct, and jsdom does no layout.
 *
 * Both earlier docblocks called this: `NotFound`'s said "if a third arrives,
 * that is the argument for teaching `Button` to render an anchor rather than a
 * fourth copy", and `Merchants`' said "this is that third site". The fourth
 * arrived and cost a defect, so the mechanism lands here instead.
 *
 * **Why not an `as` prop on `Button`.** An anchor and a button are not the same
 * control with a different tag. This has no `disabled` (an anchor cannot be
 * disabled — you remove the href), no `type`, and its ref is an
 * `HTMLAnchorElement`. Collapsing them would give every call site props that do
 * nothing on half of them, which is how `disabled` on an anchor gets written
 * and silently ignored.
 *
 * **Choosing between this and `Button`** is not a styling decision. Both of
 * `NotFound`'s recoveries are destinations, and the element has to say so: a
 * `<button>` that navigates announces itself as a control, cannot be
 * middle-clicked or opened in a new tab, has no href for the status bar or for
 * "copy link address", and is invisible to anything harvesting a page's
 * outgoing links. `App.test`'s route table asserts a LINK to `/overview` — the
 * property, not the styling — and it caught a `<button>` version once already.
 *
 * `to` routes through `react-router`; `href` renders a plain `<a>`. That is not
 * a convenience: `AppCrashed` needs a real navigation because it renders when
 * the router may be the thing that broke, and `Maintenance` links off-origin.
 */
type Common = {
  variant?: Variant
  size?: Size
  className?: string
  children?: ReactNode
  style?: React.CSSProperties
  ref?: Ref<HTMLAnchorElement>
}

type Props = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'style'> &
  Common &
  ({ to: string; href?: never } | { href: string; to?: never })

export function ButtonLink({
  variant = 'chip',
  size = 'form',
  className,
  style,
  to,
  href,
  ...rest
}: Props) {
  // Shared with `Button` — not "identical to `Button`'s", which is what this
  // said when it was a second copy. The class list, its order and the
  // hit-target floor have exactly one implementation; see buttonSkin.ts for why
  // a rule written in a docblock was not enough.
  // `btn--link` is the anchor's half of the skin: an `<a>` gets neither the
  // `inline-block` box nor the centred label a `<button>` gets from the UA,
  // so primitives.css supplies both under this class.
  const skin = useButtonSkin(
    variant,
    size,
    ['btn--link', className].filter(Boolean).join(' '),
    style,
  )

  // `className=` and `style=` written out rather than spread as `{...skin}`.
  // The spread version was correct and INVISIBLE: `InlineLink.test.tsx`'s
  // `bareLinks()` is a source-text matcher, so a `<Link>` whose className
  // arrives through a spread reads to it as a bare standalone link and it
  // failed this file. Explicit props keep both guards able to see what this
  // renders, which is the same lesson as the rest of issue 334 — a rule that
  // cannot be checked is not being enforced.
  if (to !== undefined) {
    return <Link to={to} className={skin.className} style={skin.style} {...rest} />
  }
  return <a href={href} className={skin.className} style={skin.style} {...rest} />
}
